import * as members from 'privates/privateMembers';
import {validate} from 'privates/validations/validation';

/**
 * @mixin ClickableMixin
 * @description Provides functionality for elements that can be be clicked.
 * @memberof $w
 */
class ClickableMixin {

    /**
     * @function onClick
     * @syntax
     * function onClick(handler: MouseEventHandler): Element
     * callback MouseEventHandler(event: MouseEvent, $w: Function): void
     * @summary Adds an event handler that runs when the element is clicked.
     * @description
     *  An element receives a `click` event when a user clicks on the element
     *  and releases.
     *
     *  When a user double-clicks an element, two `click` events are fired
     *  before a `doubleClick` event is also fired.
     * @param {$w.MouseEventHandler} handler The name of the function or
     *  the function expression to run when the element is clicked.
     * @returns {$w.Element} The element to which the event handler was added.
     * @snippet [ClickableMixin-onClick.es6=Get the ID of the element that was clicked]
     * @snippet [onClick-get_coordinates.es6=Get a mouse click's coordinates]
     * @eventtype click
     * @see [onDblClick( )](#onDblClick)
     * @memberof $w.ClickableMixin
     * @eventhandler
     */
    onClick(cb) {
        const compType = members._type.get(this).compType;
        if (!validate(compType, 'onClick', {onClick: cb})) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().CLICK, controllerId, cb);
        return this;
    }

    /**
     * @function onDblClick
     * @syntax
     * function onDblClick(handler: MouseEventHandler): Element
     * callback MouseEventHandler(event: MouseEvent, $w: Function): void
     * @summary Adds an event handler that runs when the element is double-clicked.
     * @description
     *  An element receives a `dblClick` event when a user double-clicks on the element
     *  and releases.
     *
     *  When a user double-clicks an element, two `click` events are fired
     *  before a `doubleClick` event is also fired.
     * @param {$w.MouseEventHandler} handler The name of the function or
     *  the function expression to run when the element is clicked.
     * @returns {$w.Element} The element to which the event handler was added.
     * @snippet [ClickableMixin-onClick.es6=Get the ID of the element that was double-clicked]
     * @see [onClick( )](#onClick)
     * @memberof $w.ClickableMixin
     * @eventtype dblClick
     * @eventhandler
     */
    onDblClick(cb) {
        const compType = members._type.get(this).compType;
        if (!validate(compType, 'onDblClick', {onDblClick: cb})) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().DBL_CLICK, controllerId, cb);
        return this;
    }
}

export default ClickableMixin;



// WEBPACK FOOTER //
// ./js/modules/ui/mixins/ClickableMixin.es6